import React, { useState, useEffect } from 'react';
import useStore from '@data/state/zustand';
import rest from '@data/restWrapper';
import dayjs from 'dayjs';
import translateBackend from '@common/backendTranslate';
import { CustomDayPicker } from '@components/DayPicker';
import { useLocation } from 'react-router-dom';
import { AccommodationOffer } from '@common/modelDefinition';
import { ReactComponent as Logo } from '../../assets/custom/logo/logo.svg';
import { openInNewTab } from '@pages/helpAndInformations/helpAndSupport';

// ---- Interfaces ----

interface PriceListEntry {
    priceListId: number | null;
    displayName: string;
    board: string;
}

interface RoomImage {
    uuid: string;
    name: string;
    type: string;
}

interface AvailableRoomType {
    roomType: string;
    numberOf: number;
    availableRoomIds: number[];
    images: RoomImage[];
    description: string;
    capacityDisplay: string;
    priceLists: PriceListEntry[];
    maxGuests: number | null;
}

interface RoomGuests {
    adults: number;
    children: number;
    childAges: number[];
    price: number;
    pricePerNight: number;
    guestTaxConfig: any;
}

interface RatePlanState {
    numberOfRooms: number;
    rooms: RoomGuests[];
    calculated: boolean;
    loading: boolean;
}

interface SelectedRoom {
    roomNumber: number;
    adults: number;
    children: number;
    childAges: number[];
    pricePerNight: number;
    totalPriceForRoom: number;
}

interface Reservation {
    roomType: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    checkIn: number | null;
    checkOut: number | null;
    adultsNumber: number;
    childrenNumber: number;
    childAges: number[];
    numberOfGuests: number;
    pricePerNight: number;
    price: number;
    roomInfoId: number;
    note: string;
    board: string;
    creditCardNumber: string;
    creditCardExpiry: string;
    guestTaxConfig?: any;
}

interface ReservationDetailsProps {
    reservations: Reservation[];
    confirmReservations: Function;
    otherSettings: any;
    loading: boolean;
    onlineReception: any;
    defaultCurrency: string;
    language: string;
    ratePlanName: string;
    onNoteChange?: (index: number, note: string) => void;
}

interface OnlineReceptionProps {
    history: any;
    match: any;
    location: any;
}

interface RoomTypeCardProps {
    roomTypeData: AvailableRoomType;
    checkIn: number;
    checkOut: number;
    numberOfNights: number;
    onlineReception: any;
    language: string;
    defaultCurrency: string;
    onSelect: (roomTypeData: AvailableRoomType, priceListIndex: number, rooms: RoomGuests[]) => void;
    getAccommodationOffer: (
        room: { adults: number; children: number; childAges: number[] },
        checkIn: number,
        checkOut: number,
        board: string,
        accommodationPriceListId: number
    ) => Promise<AccommodationOffer>;
}

// ---- Helpers ----

const getTodayAtTime = (hours: number, minutes: number = 0): number => {
    const today = new Date();
    today.setHours(hours, minutes, 0, 0);
    return today.getTime();
};

const getTomorrowAtTime = (hours: number, minutes: number = 0): number => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(hours, minutes, 0, 0);
    return tomorrow.getTime();
};

const defaultRoom = (): RoomGuests => ({
    adults: 2,
    children: 0,
    childAges: [],
    price: 0,
    pricePerNight: 0,
    guestTaxConfig: null,
});

// ---- RoomTypeCard ----

const RoomTypeCard = (props: RoomTypeCardProps) => {
    const {
        roomTypeData,
        checkIn,
        checkOut,
        numberOfNights,
        onlineReception,
        language,
        defaultCurrency,
        onSelect,
        getAccommodationOffer,
    } = props;

    const [expanded, setExpanded] = useState(false);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [cardNumberOfRooms, setCardNumberOfRooms] = useState(1);
    const [selectedPlanIndex, setSelectedPlanIndex] = useState<number | null>(null);
    const [ratePlans, setRatePlans] = useState<RatePlanState[]>(
        roomTypeData.priceLists.map(() => ({
            numberOfRooms: 1,
            rooms: [defaultRoom()],
            calculated: false,
            loading: false,
        }))
    );

    const maxCapacity = roomTypeData.maxGuests ?? 4;

    const updatePlan = (planIndex: number, patch: Partial<RatePlanState>) => {
        setRatePlans((prev) => prev.map((p, i) => (i === planIndex ? { ...p, ...patch } : p)));
    };

    const updateRoom = (planIndex: number, roomIndex: number, patch: Partial<RoomGuests>) => {
        setRatePlans((prev) =>
            prev.map((p, i) => {
                if (i !== planIndex) return p;
                const rooms = p.rooms.map((r, j) => (j === roomIndex ? { ...r, ...patch } : r));
                return { ...p, rooms, calculated: false };
            })
        );
    };

    const handleNumberOfRoomsChange = (count: number) => {
        setCardNumberOfRooms(count);
        setSelectedPlanIndex(null);
        setRatePlans((prev) =>
            prev.map((p) => {
                const rooms: RoomGuests[] = Array.from({ length: count }, (_, j) =>
                    p.rooms[j] ? { ...p.rooms[j], price: 0, pricePerNight: 0 } : defaultRoom()
                );
                return { ...p, numberOfRooms: count, rooms, calculated: false };
            })
        );
    };

    const handleAdultsChange = (planIndex: number, roomIndex: number, adults: number) => {
        const plan = ratePlans[planIndex];
        const room = plan.rooms[roomIndex];
        const maxChildren = Math.max(0, maxCapacity - adults);
        const newChildren = Math.min(room.children, maxChildren);
        updateRoom(planIndex, roomIndex, {
            adults,
            children: newChildren,
            childAges: room.childAges.slice(0, newChildren),
        });
    };

    const handleChildrenChange = (planIndex: number, roomIndex: number, children: number) => {
        const room = ratePlans[planIndex].rooms[roomIndex];
        const newChildAges = [...room.childAges];
        if (children > room.children) {
            for (let i = room.children; i < children; i++) newChildAges.push(0);
        } else {
            newChildAges.splice(children);
        }
        updateRoom(planIndex, roomIndex, { children, childAges: newChildAges });
    };

    const handleChildAgeChange = (planIndex: number, roomIndex: number, childIndex: number, age: number) => {
        const room = ratePlans[planIndex].rooms[roomIndex];
        const childAges = [...room.childAges];
        childAges[childIndex] = age;
        updateRoom(planIndex, roomIndex, { childAges });
    };

    const calculatePlan = async (planIndex: number) => {
        const plan = ratePlans[planIndex];
        const pl = roomTypeData.priceLists[planIndex];
        if (!pl.priceListId) return;

        updatePlan(planIndex, { loading: true, calculated: false });

        try {
            const board = pl.board || 'nb';
            const updatedRooms = [...plan.rooms];

            for (let i = 0; i < plan.rooms.length; i++) {
                const room = plan.rooms[i];
                const result: any = await getAccommodationOffer(
                    room,
                    checkIn,
                    checkOut,
                    board,
                    pl.priceListId as number
                );
                const totalToPay = result.invoiceItems
                    .map((item: any) => item.bruttoValueWithDiscount)
                    .reduce((a: number, b: number) => a + b, 0);
                const pricePerNight = numberOfNights > 0 ? totalToPay / numberOfNights : 0;
                updatedRooms[i] = {
                    ...updatedRooms[i],
                    price: totalToPay,
                    pricePerNight,
                    guestTaxConfig: result.guestTaxConfig,
                };
            }

            setRatePlans((prev) =>
                prev.map((p, i) =>
                    i === planIndex ? { ...p, rooms: updatedRooms, calculated: true, loading: false } : p
                )
            );
        } catch (_e) {
            updatePlan(planIndex, { loading: false });
        }
    };

    useEffect(() => {
        if (expanded) {
            roomTypeData.priceLists.forEach((_, i) => calculatePlan(i));
        }
        // eslint-disable-next-line
    }, [expanded]);

    const handleToggleExpand = () => setExpanded((prev) => !prev);

    const images = roomTypeData.images || [];
    // const dotColors = ['--hb-accent', '--hb-green', '--hb-text-3'];

    const renderCounterRow = (planIndex: number, roomIndex: number, room: RoomGuests, label: string) => {
        const maxChildren = Math.max(0, maxCapacity - room.adults);
        return (
            <React.Fragment key={roomIndex}>
                <div className="hb-counter-row">
                    <span className="hb-counter-row__label">{label}</span>
                    <div className="hb-counter-row__counters">
                        <div className="hb-counter-group">
                            <div className="hb-counter-group__label">{translateBackend('Adults', language)}</div>
                            <div className="hb-counter-controls">
                                <button
                                    type="button"
                                    className="hb-counter-btn"
                                    onClick={() =>
                                        handleAdultsChange(planIndex, roomIndex, Math.max(1, room.adults - 1))
                                    }
                                    disabled={room.adults <= 1}
                                >
                                    −
                                </button>
                                <span className="hb-counter-val">{room.adults}</span>
                                <button
                                    type="button"
                                    className="hb-counter-btn"
                                    onClick={() =>
                                        handleAdultsChange(planIndex, roomIndex, Math.min(maxCapacity, room.adults + 1))
                                    }
                                    disabled={room.adults >= maxCapacity}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <div className="hb-counter-group">
                            <div className="hb-counter-group__label">{translateBackend('Children', language)}</div>
                            <div className="hb-counter-controls">
                                <button
                                    type="button"
                                    className="hb-counter-btn"
                                    onClick={() =>
                                        handleChildrenChange(planIndex, roomIndex, Math.max(0, room.children - 1))
                                    }
                                    disabled={room.children <= 0}
                                >
                                    −
                                </button>
                                <span className="hb-counter-val">{room.children}</span>
                                <button
                                    type="button"
                                    className="hb-counter-btn"
                                    onClick={() =>
                                        handleChildrenChange(
                                            planIndex,
                                            roomIndex,
                                            Math.min(maxChildren, room.children + 1)
                                        )
                                    }
                                    disabled={room.children >= maxChildren}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {room.children > 0 && (
                    <div className="hb-child-ages">
                        {room.childAges.map((age, childIndex) => (
                            <div key={childIndex} className="hb-child-age-item">
                                <span className="hb-child-age-item__label">
                                    {translateBackend('Child', language)} {childIndex + 1}
                                </span>
                                <select
                                    className="hb-age-select"
                                    value={age}
                                    onChange={(e) =>
                                        handleChildAgeChange(planIndex, roomIndex, childIndex, parseInt(e.target.value))
                                    }
                                >
                                    {Array.from({ length: 18 }, (_, i) => (
                                        <option key={i} value={i}>
                                            {i} {translateBackend('yrs', language)}.
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                )}
            </React.Fragment>
        );
    };

    return (
        <article className="hb-card">
            <div className="hb-card__body">
                {/* Slika */}
                <div className="hb-card__image">
                    {images.length > 0 ? (
                        <>
                            <img
                                src={`/publicOnlineReceptionImage/${images[activeImageIndex].uuid}`}
                                alt={roomTypeData.roomType}
                            />
                            {images.length > 1 && (
                                <>
                                    <button
                                        className="hb-carousel__btn hb-carousel__btn--prev"
                                        onClick={() =>
                                            setActiveImageIndex((activeImageIndex - 1 + images.length) % images.length)
                                        }
                                    >
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="15 18 9 12 15 6" />
                                        </svg>
                                    </button>
                                    <button
                                        className="hb-carousel__btn hb-carousel__btn--next"
                                        onClick={() => setActiveImageIndex((activeImageIndex + 1) % images.length)}
                                    >
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="9 18 15 12 9 6" />
                                        </svg>
                                    </button>
                                    <div className="hb-carousel__dots">
                                        {images.map((_, i) => (
                                            <button
                                                key={i}
                                                className={`hb-carousel__dot${
                                                    i === activeImageIndex ? ' hb-carousel__dot--active' : ''
                                                }`}
                                                onClick={() => setActiveImageIndex(i)}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="hb-card__image--placeholder" />
                    )}
                </div>

                {/* Info */}
                <div className="hb-card__info">
                    <div className="hb-card__info-top">
                        {roomTypeData.capacityDisplay && (
                            <div className="hb-card__meta">
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                {roomTypeData.capacityDisplay}
                            </div>
                        )}
                        <h2 className="hb-card__name">{roomTypeData.roomType}</h2>
                        {roomTypeData.description && <p className="hb-card__description">{translateBackend(roomTypeData.description, language)}</p>}
                    </div>

                    <div className="hb-card__actions">
                        {expanded && roomTypeData.numberOf > 1 && onlineReception?.allowMultipleRooms && (
                            <div className="hb-card__rooms-select-wrap">
                                <span className="hb-lbl">{translateBackend('Rooms', language)}</span>
                                <select
                                    className="hb-select-rooms"
                                    value={cardNumberOfRooms}
                                    onChange={(e) => handleNumberOfRoomsChange(parseInt(e.target.value))}
                                >
                                    {Array.from({ length: roomTypeData.numberOf }, (_, i) => i + 1).map((n) => (
                                        <option key={n} value={n}>
                                            {n}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <button className={`hb-btn-toggle${expanded ? ' open' : ''}`} onClick={handleToggleExpand}>
                            {translateBackend('Show price lists', language)}
                            <i className="hb-btn-toggle__chevron">
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Rate planovi */}
            {expanded && (
                <div className={`hb-rates${roomTypeData.priceLists.length > 1 ? ' hb-rates--multi' : ''}`}>
                    {roomTypeData.priceLists.length === 0 && (
                        <div style={{ padding: '20px', color: 'var(--hb-text-3)', fontSize: 13 }}>
                            {translateBackend('No price lists configured', language)}
                        </div>
                    )}
                    {roomTypeData.priceLists.map((pl, planIndex) => {
                        const plan = ratePlans[planIndex];
                        if (!plan) return null;
                        const totalPrice = plan.rooms.reduce((sum, r) => sum + r.price, 0);
                        const dotClass = planIndex % 2 === 0 ? 'hb-rate__dot--blue' : 'hb-rate__dot--green';

                        return (
                            <div key={planIndex} className="hb-rate">
                                {/* Naziv cjenika */}
                                <div className="hb-rate__top">
                                    <span className={`hb-rate__dot ${dotClass}`} />
                                    <div>
                                        <div className="hb-rate__name">
                                            {pl.displayName || translateBackend('Standard rate', language)}
                                        </div>
                                    </div>
                                </div>

                                {/* Counteri + cijena + gumb */}
                                <div className="hb-rate__bottom">
                                    <div className="hb-counters-list">
                                        {plan.rooms.map((room, roomIndex) =>
                                            renderCounterRow(
                                                planIndex,
                                                roomIndex,
                                                room,
                                                plan.numberOfRooms > 1
                                                    ? `${translateBackend('Room', language)} ${roomIndex + 1}`
                                                    : translateBackend('Guests', language)
                                            )
                                        )}
                                    </div>

                                    <div className="hb-rate__price-action">
                                        {plan.calculated && (
                                            <div className="hb-price">
                                                <div className="hb-price__main">
                                                    {totalPrice.toFixed(2).split('.')[0]}
                                                    <small>.{totalPrice.toFixed(2).split('.')[1]}</small>
                                                    <span className="hb-price__currency">{defaultCurrency}</span>
                                                </div>
                                                <div className="hb-price__per">
                                                    {plan.numberOfRooms > 1
                                                        ? plan.rooms.map((room, roomIndex) => (
                                                              <div key={roomIndex}>
                                                                  {translateBackend('Room', language)} {roomIndex + 1}:{' '}
                                                                  {room.pricePerNight.toFixed(2)} {defaultCurrency} /{' '}
                                                                  {translateBackend('night', language)}
                                                              </div>
                                                          ))
                                                        : `${plan.rooms[0].pricePerNight.toFixed(2)} ${defaultCurrency} / ${translateBackend('night', language)}`}
                                                </div>
                                            </div>
                                        )}

                                        {plan.loading ? (
                                            <button className="hb-btn-offer" disabled>
                                                <span className="hb-btn__spinner" />{' '}
                                                {translateBackend('Calculating...', language)}
                                            </button>
                                        ) : plan.calculated && selectedPlanIndex === planIndex ? (
                                            <button className="hb-btn-select">
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                                {translateBackend('Selected', language)}
                                            </button>
                                        ) : plan.calculated ? (
                                            <button
                                                className="hb-btn-offer"
                                                onClick={() => setSelectedPlanIndex(planIndex)}
                                            >
                                                {translateBackend('Select', language)}
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </button>
                                        ) : (
                                            <button
                                                className="hb-btn-offer"
                                                disabled={!pl.priceListId}
                                                onClick={() => calculatePlan(planIndex)}
                                            >
                                                {translateBackend('Offer', language)}
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {selectedPlanIndex !== null && ratePlans[selectedPlanIndex]?.calculated && (
                        <div className="hb-rates__next-bar">
                            <div className="hb-rates__next-bar__info">
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--hb-green)"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span>
                                    {roomTypeData.roomType}
                                    {roomTypeData.priceLists[selectedPlanIndex]?.displayName &&
                                        ` · ${roomTypeData.priceLists[selectedPlanIndex].displayName}`}
                                    {` · ${ratePlans[selectedPlanIndex].rooms.reduce((sum, r) => sum + r.price, 0).toFixed(2)} ${defaultCurrency}`}
                                </span>
                            </div>
                            <button
                                className="hb-btn-next-step"
                                onClick={() =>
                                    onSelect(
                                        roomTypeData,
                                        selectedPlanIndex,
                                        ratePlans[selectedPlanIndex].rooms
                                    )
                                }
                            >
                                {translateBackend('Next step', language)}
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </article>
    );
};

// ---- Main OnlineReception ----

const OnlineReception = (props: OnlineReceptionProps) => {
    const location = useLocation();
    const [checkIn, setCheckIn] = useState<number | null>(() => getTodayAtTime(14));
    const [checkOut, setCheckOut] = useState<number | null>(() => getTomorrowAtTime(10));
    const [numberOfNights, setNumberOfNights] = useState<number>(0);
    const [availableRooms, setAvailableRooms] = useState<AvailableRoomType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [showBookingForm, setShowBookingForm] = useState<boolean>(false);
    const [showReservationDetails, setShowReservationDetails] = useState<boolean>(false);
    const [priceCalculated, setPriceCalculated] = useState<boolean>(false);
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [selectedRooms, setSelectedRooms] = useState<SelectedRoom[]>([]);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [selectedRoomTypeData, setSelectedRoomTypeData] = useState<AvailableRoomType | null>(null);
    const [selectedRatePlanName, setSelectedRatePlanName] = useState<string>('');

    const [guestName, setGuestName] = useState<string>('');
    const [guestLastName, setGuestLastName] = useState<string>('');
    const [guestEmail, setGuestEmail] = useState<string>('');
    const [guestPhoneNumber, setGuestPhoneNumber] = useState<string>('');
    const [guestCreditCardNumber, setGuestCreditCardNumber] = useState<string>('');
    const [guestCreditCardExpiry, setGuestCreditCardExpiry] = useState<string>('');
    const [emailIsRequired, setEmailIsRequired] = useState<boolean>(false);
    const [creditCardNumberIsRequired, setCreditCardNumberIsRequired] = useState<boolean>(false);
    const [creditCardExpiryIsRequired, setCreditCardExpiryIsRequired] = useState<boolean>(false);

    const [isConfirmationView, setIsConfirmationView] = useState(false);
    const [REACT_PUBLIC_RECAPTCHA_SITE_KEY, setReactPublicRecaptchaSiteKey] = useState<string | null>(null);

    const onlineReception = useStore((appState: any) => appState.onlineReception);
    const otherSettings = useStore((appState: any) => appState.otherSettings);
    const defaultCurrency: string = useStore((appState: any) => appState.defaultCurrency);
    const locale: string = useStore((appState: any) => appState.locale);

    const query = new URLSearchParams(location.search);
    const verificationToken = query.get('verificationToken');
    const lang = query.get('lang');
    const language = lang ? lang : locale ? locale : 'en';

    useEffect(() => {
        if (verificationToken) setIsConfirmationView(true);
        // eslint-disable-next-line
    }, [verificationToken]);

    useEffect(() => {
        const initializeRecaptcha = async () => {
            try {
                const response = await rest('/captchaSiteKey', 'GET');
                if (!response) throw new Error('Invalid CAPTCHA key response');
                setReactPublicRecaptchaSiteKey(response);
                const script = document.createElement('script');
                script.src = `https://www.google.com/recaptcha/api.js?render=${response}`;
                script.async = true;
                document.body.appendChild(script);
                return () => {
                    document.body.removeChild(script);
                };
            } catch (e) {
                console.error('CAPTCHA initialization failed:', e);
            }
        };
        initializeRecaptcha();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (checkIn && checkOut) {
            const diffDays = Math.ceil(Math.abs(checkOut - checkIn) / (1000 * 60 * 60 * 24));
            setNumberOfNights(diffDays);
        } else {
            setNumberOfNights(0);
        }
    }, [checkIn, checkOut]);

    useEffect(() => {
        if (reservations.length > 0) {
            setReservations((prev) =>
                prev.map((r) => ({
                    ...r,
                    firstName: guestName,
                    lastName: guestLastName,
                    email: guestEmail,
                    phoneNumber: guestPhoneNumber,
                    creditCardNumber: guestCreditCardNumber,
                    creditCardExpiry: guestCreditCardExpiry,
                }))
            );
        }
        // eslint-disable-next-line
    }, [guestName, guestLastName, guestEmail, guestPhoneNumber, guestCreditCardNumber, guestCreditCardExpiry]);

    const getHourAndMinuteObj = (time: string) => {
        const parts = time ? time.split(':') : ['14', '0'];
        return { hour: Number(parts[0]), minute: Number(parts[1]) };
    };

    const handleCheckInDayChange = (day: Date) => {
        const { checkInTime = '14:00', checkOutTime = '10:00' } = otherSettings || {};
        const ci = getHourAndMinuteObj(checkInTime);
        const d = new Date(day);
        d.setHours(ci.hour, ci.minute, 0, 0);
        setCheckIn(d.getTime());
        if (!checkOut || d.getTime() >= checkOut) {
            const next = new Date(d);
            next.setDate(next.getDate() + 1);
            const co = getHourAndMinuteObj(checkOutTime);
            next.setHours(co.hour, co.minute, 0, 0);
            setCheckOut(next.getTime());
        }
        setAvailableRooms([]);
        setPriceCalculated(false);
        setShowBookingForm(false);
    };

    const handleCheckOutDayChange = (day: Date) => {
        const { checkOutTime = '10:00' } = otherSettings || {};
        const co = getHourAndMinuteObj(checkOutTime);
        const d = new Date(day);
        d.setHours(co.hour, co.minute, 0, 0);
        setCheckOut(d.getTime());
        setAvailableRooms([]);
        setPriceCalculated(false);
        setShowBookingForm(false);
    };

    const getMinStayForDateRange = (ci: number, co: number, minStayPeriods: any[]): number => {
        if (!minStayPeriods || minStayPeriods.length === 0) return 1;
        const dates: Date[] = [];
        const cur = new Date(ci);
        while (cur < new Date(co)) {
            dates.push(new Date(cur));
            cur.setDate(cur.getDate() + 1);
        }
        let max = 1;
        for (const date of dates) {
            const dd = date.getDate().toString().padStart(2, '0');
            const mm = (date.getMonth() + 1).toString().padStart(2, '0');
            const dateStr = `${dd}/${mm}`;
            const dow = date.getDay();
            for (const period of minStayPeriods) {
                if (!period.fromDate || !period.toDate || !period.minStay) continue;
                const pf = new Date(period.fromDate);
                const pt = new Date(period.toDate);
                const pfStr = `${pf.getDate().toString().padStart(2, '0')}/${(pf.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}`;
                const ptStr = `${pt.getDate().toString().padStart(2, '0')}/${(pt.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}`;
                const parseD = (s: string) => {
                    const [d2, m2] = s.split('/').map(Number);
                    return m2 * 100 + d2;
                };
                const dn = parseD(dateStr),
                    fn = parseD(pfStr),
                    tn = parseD(ptStr);
                const inPeriod = fn > tn ? dn >= fn || dn <= tn : dn >= fn && dn <= tn;
                if (inPeriod) {
                    const apply = period.applyToAllDays ?? true;
                    if (apply || (period.daysOfWeek || []).includes(dow)) {
                        if (period.minStay > max) max = period.minStay;
                    }
                }
            }
        }
        return max;
    };

    const checkAvailability = async () => {
        if (!checkIn || !checkOut) return;

        const minStayDays = getMinStayForDateRange(checkIn, checkOut, onlineReception?.minStayPeriods);
        const diffDays = Math.ceil(Math.abs(checkOut - checkIn) / (1000 * 60 * 60 * 24));
        if (diffDays < minStayDays) {
            setError(
                `${translateBackend('Minimum stay is', language)} ${minStayDays} ${translateBackend(
                    'night(s). Please adjust your dates',
                    language
                )}.`
            );
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');
        setAvailableRooms([]);
        setShowBookingForm(false);
        setPriceCalculated(false);

        try {
            const grecaptcha = (window as any).grecaptcha;
            if (!grecaptcha) throw new Error('reCAPTCHA not loaded');

            const roomTypesSettings = onlineReception?.roomTypes || {};
            const roomTypeNames = Object.keys(roomTypesSettings);

            const results = await Promise.all(
                roomTypeNames.map(async (rtName) => {
                    const rtSettings = roomTypesSettings[rtName];
                    try {
                        const token = await grecaptcha.execute(REACT_PUBLIC_RECAPTCHA_SITE_KEY, {
                            action: 'getRoomsAvailForOnlineReception',
                        });
                        const res = await rest('/getRoomsAvailForOnlineReception', 'POST', {
                            roomType: rtName,
                            roomInfoIds: rtSettings.roomInfoIds,
                            checkIn,
                            checkOut,
                            priceListId: rtSettings.priceLists?.[0]?.priceListId ?? null,
                            token,
                        });
                        if (!res || res.numberOf <= 0) return null;

                        let images: RoomImage[] = [];
                        try {
                            images =
                                (await rest(`/publicOnlineReceptionImages/${encodeURIComponent(rtName)}`, 'GET')) || [];
                        } catch (_e) {}

                        const priceLists: PriceListEntry[] = (rtSettings.priceLists || []).map((pl: any) => ({
                            priceListId: pl.priceListId ?? null,
                            displayName: pl.displayName || '',
                            board: pl.board || 'nb',
                        }));

                        const available: AvailableRoomType = {
                            roomType: rtName,
                            numberOf: res.numberOf,
                            availableRoomIds: res.availableRoomIds || rtSettings.roomInfoIds || [],
                            images,
                            description: rtSettings.description || '',
                            capacityDisplay: rtSettings.capacityDisplay || '',
                            priceLists,
                            maxGuests: rtSettings.maxGuests ?? null,
                        };
                        return available;
                    } catch (_e) {
                        return null;
                    }
                })
            );

            const filtered = results.filter(Boolean) as AvailableRoomType[];
            setAvailableRooms(filtered);

            if (filtered.length === 0) {
                setError(translateBackend('No available rooms for the selected period', language));
            }
        } catch (err) {
            setError(translateBackend('Error checking availability', language));
        } finally {
            setLoading(false);
        }
    };

    const buildAdultsArray = (count: number) =>
        Array.from({ length: count }, () => ({ visaInfoId: null, fixedTaxId: null, fixedTaxId2: null }));

    const buildChildrenArray = (childAges: number[]) =>
        childAges.map((age) => ({ age, visaInfoId: null, fixedTaxId: null, fixedTaxId2: null }));

    const getAccommodationOffer = async (
        room: { adults: number; children: number; childAges: number[] },
        ci: number,
        co: number,
        board: string,
        accommodationPriceListId: number
    ): Promise<AccommodationOffer> => {
        const grecaptcha = (window as any).grecaptcha;
        if (!grecaptcha) throw new Error('reCAPTCHA not loaded');
        const token = await grecaptcha.execute(REACT_PUBLIC_RECAPTCHA_SITE_KEY, {
            action: 'getAccommodationOfferForOnlineReception',
        });
        const res = await rest('/getAccommodationOfferForOnlineReception', 'POST', {
            reservation: {
                accommodationPriceListId,
                checkInTimestamp: ci,
                checkOutTimestamp: co,
                adultsNumber: room.adults,
                childrenNumber: room.children,
                board,
                guestTaxConfig: {
                    adults: buildAdultsArray(room.adults),
                    children: buildChildrenArray(room.childAges),
                },
            },
            token,
        });
        return {
            ...res,
            guestTaxConfig: {
                adults: buildAdultsArray(room.adults),
                children: buildChildrenArray(room.childAges),
            },
        };
    };

    const handleSelectRoom = (roomTypeData: AvailableRoomType, priceListIndex: number, rooms: RoomGuests[]) => {
        const pl = roomTypeData.priceLists[priceListIndex];
        setSelectedRoomTypeData(roomTypeData);
        setSelectedRatePlanName(pl?.displayName || '');

        const newSelectedRooms: SelectedRoom[] = rooms.map((r, i) => ({
            roomNumber: i + 1,
            adults: r.adults,
            children: r.children,
            childAges: r.childAges,
            pricePerNight: r.pricePerNight,
            totalPriceForRoom: r.price,
        }));

        const newReservations: Reservation[] = rooms.map((r, i) => ({
            roomType: roomTypeData.roomType,
            firstName: guestName,
            lastName: guestLastName,
            email: guestEmail,
            phoneNumber: guestPhoneNumber,
            creditCardNumber: guestCreditCardNumber,
            creditCardExpiry: guestCreditCardExpiry,
            checkIn,
            checkOut,
            adultsNumber: r.adults,
            childrenNumber: r.children,
            childAges: r.childAges,
            numberOfGuests: r.adults + r.children,
            pricePerNight: r.pricePerNight,
            price: r.price,
            roomInfoId: roomTypeData.availableRoomIds[i] ?? roomTypeData.availableRoomIds[0],
            note: '',
            board: pl?.board || 'nb',
            guestTaxConfig: r.guestTaxConfig,
        }));

        setSelectedRooms(newSelectedRooms);
        setReservations(newReservations);
        setTotalPrice(rooms.reduce((sum, r) => sum + r.price, 0));
        setPriceCalculated(true);
        setShowBookingForm(true);
        setShowReservationDetails(false);

        setTimeout(() => {
            document.querySelector('.hb-booking')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleReservationNoteChange = (index: number, note: string) => {
        setReservations((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], note };
            return updated;
        });
    };

    const formatCreditCardNumber = (value: string): string => {
        const numbers = value.replace(/\D/g, '');
        return numbers.replace(/(\d{4})(?=\d)/g, '$1 ');
    };

    const handleCreditCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGuestCreditCardNumber(formatCreditCardNumber(e.target.value));
        setCreditCardNumberIsRequired(false);
    };

    const handleCreditCardExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/[^\d/]/g, '');
        if (value.length === 2 && !value.includes('/')) value = value + '/';
        if (value.length > 5) value = value.substring(0, 5);
        setGuestCreditCardExpiry(value);
        setCreditCardExpiryIsRequired(false);
    };

    const validateCreditCardExpiry = (expiry: string): boolean => {
        if (!expiry || !expiry.includes('/')) return false;
        const [monthStr, yearStr] = expiry.split('/');
        const month = parseInt(monthStr, 10);
        const year = parseInt('20' + yearStr, 10);
        if (month < 1 || month > 12) return false;
        const now = new Date();
        if (year < now.getFullYear()) return false;
        if (year === now.getFullYear() && month < now.getMonth() + 1) return false;
        return true;
    };

    const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const validateForm = (): boolean => {
        let hasError = false;
        if (!validateEmail(guestEmail)) {
            setError(translateBackend('Please enter a valid email address', language));
            setEmailIsRequired(true);
            hasError = true;
        } else {
            setEmailIsRequired(false);
        }
        if (onlineReception?.requireCreditCard) {
            if (!guestCreditCardNumber || guestCreditCardNumber.replace(/\s/g, '').length < 16) {
                setError(translateBackend('Please enter a valid credit card number', language));
                setCreditCardNumberIsRequired(true);
                hasError = true;
            } else {
                setCreditCardNumberIsRequired(false);
            }
            if (!validateCreditCardExpiry(guestCreditCardExpiry)) {
                setError(translateBackend('Please enter a valid expiration date (MM/YY)', language));
                setCreditCardExpiryIsRequired(true);
                hasError = true;
            } else {
                setCreditCardExpiryIsRequired(false);
            }
        }
        if (!hasError) setError('');
        return !hasError;
    };

    const confirmReservations = async () => {
        setEmailIsRequired(false);
        setCreditCardNumberIsRequired(false);
        setCreditCardExpiryIsRequired(false);
        setError('');
        if (!validateForm()) return;
        setLoading(true);
        try {
            const grecaptcha = (window as any).grecaptcha;
            if (!grecaptcha) throw new Error('reCAPTCHA not loaded');
            const token = await grecaptcha.execute(REACT_PUBLIC_RECAPTCHA_SITE_KEY, {
                action: 'saveOnlineReceptionReservations',
            });
            const reservationsWithPlan = reservations.map((r) => ({
                ...r,
                note: selectedRatePlanName ? `${selectedRatePlanName}${r.note ? '\n' + r.note : ''}` : r.note,
            }));
            const result = await rest('/saveOnlineReceptionReservations', 'POST', {
                reservations: reservationsWithPlan,
                token,
            });

            if (result && result.success === true) {
                setSuccessMessage(
                    translateBackend(
                        "The reservation has been successfully received! We have sent you a confirmation email. Please check your inbox - and don't forget to look in your spam or junk folder, just in case.",
                        language
                    )
                );
                setAvailableRooms([]);
                setGuestName('');
                setGuestLastName('');
                setGuestEmail('');
                setGuestPhoneNumber('');
                setGuestCreditCardNumber('');
                setGuestCreditCardExpiry('');
                setReservations([]);
                setShowBookingForm(false);
                setShowReservationDetails(false);
                setPriceCalculated(false);
                setTotalPrice(0);
            } else {
                setError(translateBackend('An error occurred while making the reservation', language));
            }
        } catch (_err) {
            setError(translateBackend('An error occurred while making the reservation', language));
        } finally {
            setLoading(false);
        }
    };

    const handleGuestReservationConfirmation = async () => {
        try {
            setLoading(true);
            setError('');
            setSuccessMessage('');
            const grecaptcha = (window as any).grecaptcha;
            if (!grecaptcha) throw new Error('reCAPTCHA not loaded');
            const token = await grecaptcha.execute(REACT_PUBLIC_RECAPTCHA_SITE_KEY, {
                action: 'onlineResConfirmationByGuest',
            });
            const response = await rest('/onlineResConfirmationByGuest', 'POST', { verificationToken, token });
            if (response.success) {
                let seconds = 3;
                const tick = () => {
                    setSuccessMessage(
                        `${translateBackend('Reservation successfully confirmed!', language)} ${translateBackend(
                            'Redirecting in',
                            language
                        )} ${seconds}...`
                    );
                    if (seconds <= 0) {
                        window.location.href = onlineReception.hotelDomain;
                        return;
                    }
                    seconds--;
                    setTimeout(tick, 1000);
                };
                tick();
            } else {
                setError(response.message || translateBackend('Failed to confirm reservation', language));
            }
        } catch (_e) {
            setError(translateBackend('Error confirming reservation', language));
        } finally {
            setLoading(false);
        }
    };

    if (onlineReception && onlineReception.enabled === false) {
        return (
            <div className="hb-page">
                <div className="hb-disabled">
                    {translateBackend(
                        'Online booking is currently disabled. Please contact the property directly for reservations.',
                        language
                    )}
                </div>
            </div>
        );
    }

    const nightsLabel =
        numberOfNights === 1
            ? `${numberOfNights} ${translateBackend('night', language)}`
            : `${numberOfNights} ${translateBackend('nights', language)}`;

    return (
        <div className="hb-page">
            {isConfirmationView ? (
                <div className="hb-wrap hb-confirm">
                    <div className="hb-confirm__box">
                        {successMessage ? (
                            <>
                                <div className="hb-confirm__icon hb-confirm__icon--success">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <h2 className="hb-confirm__title">
                                    {translateBackend('Reservation Confirmed', language)}
                                </h2>
                                <p className="hb-confirm__text">{successMessage}</p>
                            </>
                        ) : error ? (
                            <>
                                <div className="hb-confirm__icon hb-confirm__icon--error">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="15" y1="9" x2="9" y2="15" />
                                        <line x1="9" y1="9" x2="15" y2="15" />
                                    </svg>
                                </div>
                                <h2 className="hb-confirm__title">
                                    {translateBackend('Confirmation Failed', language)}
                                </h2>
                                <p className="hb-confirm__text">{error}</p>
                            </>
                        ) : (
                            <>
                                <div className="hb-confirm__icon hb-confirm__icon--pending">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                    </svg>
                                </div>
                                <h2 className="hb-confirm__title">
                                    {translateBackend('Confirm Your Reservation', language)}
                                </h2>
                                <p className="hb-confirm__text">
                                    {translateBackend(
                                        'Please confirm your reservation by clicking the button below.',
                                        language
                                    )}
                                </p>
                                <button
                                    className="hb-btn-reserve"
                                    onClick={handleGuestReservationConfirmation}
                                    disabled={loading}
                                >
                                    {loading
                                        ? translateBackend('Confirming...', language)
                                        : translateBackend('Confirm Reservation', language)}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {/* Header */}
                    <header className="hb-header">
                        <div className="hb-wrap">
                            <div className="hb-header__top">
                                <div className="hb-header__logo">
                                    <div className="hb-header__logo-icon">
                                        <i className="fa fa-hotel" />
                                    </div>
                                    <span className="hb-header__logo-name">
                                        {onlineReception?.propertyName || 'HotelBook'}
                                    </span>
                                </div>
                                <div className="side-menu-logo web-reception" onClick={()=>openInNewTab("https://hologic.io/")}>
                                    <Logo></Logo>
                                </div>
                            </div>
                            <div className="hb-search__inner">
                                <div className="hb-search__field">
                                    <span className="hb-search__label">{translateBackend('Check in', language)}</span>
                                    <CustomDayPicker
                                        value={checkIn ?? new Date().getTime()}
                                        onSelect={(d: any) => handleCheckInDayChange(d)}
                                        disabledBefore={new Date()}
                                        height="38px"
                                        fontSize="13px"
                                        width='auto'
                                    />
                                </div>
                                <div className="hb-search__field">
                                    <span className="hb-search__label">{translateBackend('Check Out', language)}</span>
                                    <CustomDayPicker
                                        value={checkOut ?? new Date().getTime()}
                                        onSelect={(d: any) => handleCheckOutDayChange(d)}
                                        disabledBefore={
                                            checkIn
                                                ? new Date(checkIn + 24 * 60 * 60 * 1000)
                                                : new Date(new Date().setHours(0, 0, 0, 0) + 24 * 60 * 60 * 1000)
                                        }
                                        height="38px"
                                        fontSize="13px"
                                        width='auto'
                                    />
                                </div>
                                <button
                                    className="hb-btn-search"
                                    disabled={loading || !checkIn || !checkOut}
                                    onClick={checkAvailability}
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="11" cy="11" r="8" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    {loading
                                        ? translateBackend('Searching...', language)
                                        : translateBackend('Search', language)}
                                </button>
                            </div>
                        </div>
                    </header>

                    <div className={`hb-wrap${availableRooms.length > 0 || showBookingForm ? ' hb-content-bg' : ''}`}>
                        {/* Results header */}
                        {availableRooms.length > 0 && !showBookingForm && (
                            <div className="hb-results-header">
                                <div>
                                    <h1 className="hb-results-header__title">
                                        {translateBackend('Available rooms', language)}
                                    </h1>
                                    <p className="hb-results-header__sub">
                                        {checkIn ? dayjs(checkIn).format('D MMM') : ''} -{' '}
                                        {checkOut ? dayjs(checkOut).format('D MMM') : ''} · {nightsLabel}
                                    </p>
                                </div>
                                <span className="hb-results-header__count">
                                    {availableRooms.length} {translateBackend('results', language)}
                                </span>
                            </div>
                        )}

                        {/* Room cards */}
                        {availableRooms.length > 0 && !showBookingForm && (
                            <main className="hb-rooms">
                                {availableRooms.map((roomTypeData, index) => (
                                    <RoomTypeCard
                                        key={index}
                                        roomTypeData={roomTypeData}
                                        checkIn={checkIn!}
                                        checkOut={checkOut!}
                                        numberOfNights={numberOfNights}
                                        onlineReception={onlineReception}
                                        language={language}
                                        defaultCurrency={defaultCurrency}
                                        onSelect={handleSelectRoom}
                                        getAccommodationOffer={getAccommodationOffer}
                                    />
                                ))}
                            </main>
                        )}

                        {/* Booking form */}
                        {showBookingForm && priceCalculated && selectedRoomTypeData && (
                            <div className="hb-booking">
                                {/* Summary */}
                                <div
                                    className="hb-summary"
                                    style={{ display: showReservationDetails ? 'none' : undefined }}
                                >
                                    <div className="hb-summary__header">
                                        <p className="hb-summary__room-name">
                                            {selectedRoomTypeData.roomType}
                                            {selectedRatePlanName && ` · ${selectedRatePlanName}`}
                                        </p>
                                        <p className="hb-summary__meta">
                                            {selectedRooms.length}{' '}
                                            {selectedRooms.length === 1
                                                ? translateBackend('room', language)
                                                : translateBackend('_rooms', language)}{' '}
                                            · {nightsLabel}
                                        </p>
                                    </div>
                                    <div className="hb-summary__prices">
                                        {selectedRooms.map((room, i) => (
                                            <div key={i} className="hb-summary__row">
                                                <span>
                                                    {selectedRooms.length > 1
                                                        ? `${translateBackend('Room', language)} ${
                                                              room.roomNumber
                                                          } - ${translateBackend('Price per night', language)}`
                                                        : translateBackend('Price per night', language)}
                                                </span>
                                                <span>
                                                    {(room.pricePerNight || 0).toFixed(2)} {defaultCurrency}
                                                </span>
                                            </div>
                                        ))}
                                        <div className="hb-summary__row hb-summary__row--total">
                                            <span>{translateBackend('Total price', language)}</span>
                                            <span>
                                                {(totalPrice || 0).toFixed(2)} {defaultCurrency}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Guest form */}
                                <div
                                    className="hb-guest-form"
                                    style={{ display: showReservationDetails ? 'none' : undefined }}
                                >
                                    <p className="hb-guest-form__title">
                                        {translateBackend('Guest informations', language)}
                                    </p>
                                    <div className="hb-form-grid">
                                        <div className="hb-field">
                                            <label className="hb-field__label">
                                                {translateBackend('First name', language)}{' '}
                                                <span className="req">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="hb-input"
                                                value={guestName}
                                                onChange={(e) => setGuestName(e.target.value)}
                                                placeholder={translateBackend('Your first name', language)}
                                            />
                                        </div>
                                        <div className="hb-field">
                                            <label className="hb-field__label">
                                                {translateBackend('Last name', language)} <span className="req">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="hb-input"
                                                value={guestLastName}
                                                onChange={(e) => setGuestLastName(e.target.value)}
                                                placeholder={translateBackend('Your last name', language)}
                                            />
                                        </div>
                                        <div className="hb-field">
                                            <label className="hb-field__label">
                                                {translateBackend('Email', language)} <span className="req">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                className={`hb-input${emailIsRequired ? ' hb-input--error' : ''}`}
                                                value={guestEmail}
                                                onChange={(e) => {
                                                    setGuestEmail(e.target.value);
                                                    if (e.target.value && !validateEmail(e.target.value)) {
                                                        setEmailIsRequired(true);
                                                    } else {
                                                        setEmailIsRequired(false);
                                                        setError('');
                                                    }
                                                }}
                                                placeholder={translateBackend('your@email.com', language)}
                                            />
                                        </div>
                                        <div className="hb-field">
                                            <label className="hb-field__label">
                                                {translateBackend('Phone number', language)}
                                            </label>
                                            <input
                                                type="tel"
                                                className="hb-input"
                                                value={guestPhoneNumber}
                                                onChange={(e) => setGuestPhoneNumber(e.target.value)}
                                                placeholder={translateBackend('Your phone number', language)}
                                            />
                                        </div>
                                        <div className="hb-field">
                                            <label className="hb-field__label">
                                                {translateBackend('Credit Card Number', language)}
                                                {onlineReception?.requireCreditCard && <span className="req"> *</span>}
                                            </label>
                                            <input
                                                type="text"
                                                className={`hb-input${
                                                    creditCardNumberIsRequired ? ' hb-input--error' : ''
                                                }`}
                                                value={guestCreditCardNumber}
                                                onChange={handleCreditCardNumberChange}
                                                placeholder="1234 5678 9012 3456"
                                                maxLength={19}
                                            />
                                            {!onlineReception?.requireCreditCard && (
                                                <span className="hb-field__hint">
                                                    ({translateBackend('optional', language)})
                                                </span>
                                            )}
                                        </div>
                                        <div className="hb-field">
                                            <label className="hb-field__label">
                                                {translateBackend('Expiration Date', language)} (MM/YY)
                                                {onlineReception?.requireCreditCard && <span className="req"> *</span>}
                                            </label>
                                            <input
                                                type="text"
                                                className={`hb-input${
                                                    creditCardExpiryIsRequired ? ' hb-input--error' : ''
                                                }`}
                                                value={guestCreditCardExpiry}
                                                onChange={handleCreditCardExpiryChange}
                                                placeholder="MM/YY"
                                                maxLength={5}
                                            />
                                            {!onlineReception?.requireCreditCard && (
                                                <span className="hb-field__hint">
                                                    ({translateBackend('optional', language)})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {showReservationDetails ? (
                                    <ReservationDetails
                                        reservations={reservations}
                                        confirmReservations={confirmReservations}
                                        otherSettings={otherSettings}
                                        loading={loading}
                                        onlineReception={onlineReception}
                                        defaultCurrency={defaultCurrency}
                                        language={language}
                                        ratePlanName={selectedRatePlanName}
                                        onNoteChange={handleReservationNoteChange}
                                    />
                                ) : (
                                    <div className="hb-form-footer">
                                        <button
                                            className="hb-btn-back"
                                            onClick={() => {
                                                setShowBookingForm(false);
                                                setShowReservationDetails(false);
                                                setPriceCalculated(false);
                                            }}
                                        >
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <line x1="19" y1="12" x2="5" y2="12" />
                                                <polyline points="12 19 5 12 12 5" />
                                            </svg>
                                            {translateBackend('Back', language)}
                                        </button>
                                        <button
                                            className="hb-btn-next"
                                            disabled={loading || !guestName || !guestLastName || !guestEmail}
                                            onClick={() => {
                                                if (validateForm()) setShowReservationDetails(true);
                                            }}
                                        >
                                            {loading
                                                ? translateBackend('Reserving...', language)
                                                : translateBackend('Next', language)}
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Messages */}
                        {error && (
                            <div className="hb-message hb-message--error">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="12" />
                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                <span>{translateBackend(error, language)}</span>
                            </div>
                        )}
                        {successMessage && (
                            <div className="hb-message hb-message--success">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                                <span>{translateBackend(successMessage, language)}</span>
                            </div>
                        )}

                        <div className="mt-auto flex-center-center"></div>
                    </div>
                    <div className='h-20vh'></div>
                </>
            )}
        </div>
    );
};

// ---- ReservationDetails ----

const ReservationDetails = (props: ReservationDetailsProps) => {
    const {
        reservations,
        confirmReservations,
        otherSettings,
        loading,
        onlineReception,
        defaultCurrency,
        language,
        ratePlanName,
        onNoteChange,
    } = props;

    return (
        <div className="hb-details">
            <p className="hb-details__title">{translateBackend('Reservation summary', language)}</p>
            <div>
                {reservations.map((reservation, index) => (
                    <div key={index} className="hb-details__card">
                        <div className="hb-details__card-header">
                            <h5 className="hb-details__room-name">{reservation.roomType}</h5>
                            <span className="hb-details__guests-badge">
                                {reservation.adultsNumber}{' '}
                                {reservation.adultsNumber === 1
                                    ? translateBackend('adult', language)
                                    : translateBackend('adults', language)}
                                {reservation.childrenNumber > 0 && (
                                    <>
                                        , {reservation.childrenNumber}{' '}
                                        {reservation.childrenNumber === 1
                                            ? translateBackend('child', language)
                                            : translateBackend('children', language)}
                                    </>
                                )}
                            </span>
                        </div>
                        <div className="hb-details__body">
                            <div className="hb-details__row">
                                <span>{translateBackend('Arrival', language)}</span>
                                <span>
                                    {reservation.checkIn
                                        ? dayjs(reservation.checkIn).format(
                                              otherSettings?.dateFormat
                                                  ? otherSettings.dateFormat + ' HH:mm'
                                                  : 'DD/MM/YYYY HH:mm'
                                          )
                                        : '-'}
                                </span>
                            </div>
                            <div className="hb-details__row">
                                <span>{translateBackend('Departure', language)}</span>
                                <span>
                                    {reservation.checkOut
                                        ? dayjs(reservation.checkOut).format(
                                              otherSettings?.dateFormat
                                                  ? otherSettings.dateFormat + ' HH:mm'
                                                  : 'DD/MM/YYYY HH:mm'
                                          )
                                        : '-'}
                                </span>
                            </div>
                            <div className="hb-details__row">
                                <span>{translateBackend('Guest', language)}</span>
                                <span>
                                    {reservation.firstName} {reservation.lastName}
                                </span>
                            </div>
                            <div className="hb-details__row">
                                <span>{translateBackend('Email', language)}</span>
                                <span>{reservation.email}</span>
                            </div>
                            <div className="hb-details__row">
                                <span>{translateBackend('Price per night', language)}</span>
                                <span>
                                    {(reservation.pricePerNight || 0).toFixed(2)} {defaultCurrency}
                                </span>
                            </div>
                            <div className="hb-details__row hb-details__row--total">
                                <span>{translateBackend('Total price', language)}</span>
                                <span>
                                    {(reservation.price || 0).toFixed(2)} {defaultCurrency}
                                </span>
                            </div>

                            <div>
                                <label className="hb-details__note-label">
                                    {translateBackend('Note (optional)', language)}
                                </label>
                                {ratePlanName && <div className="hb-details__note-prefix">{ratePlanName}</div>}
                                <textarea
                                    className="hb-textarea"
                                    value={reservation.note || ''}
                                    onChange={(e) => onNoteChange && onNoteChange(index, e.target.value)}
                                    placeholder={translateBackend('Add any special requests or notes...', language)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {onlineReception?.requireCreditCard && (
                <div className="hb-details__body" style={{ marginBottom: 12 }}>
                    <div className="hb-details__row">
                        <span>{translateBackend('Credit Card Number', language)}</span>
                        <span>{reservations[0].creditCardNumber}</span>
                    </div>
                    <div className="hb-details__row">
                        <span>{translateBackend('Expires', language)}</span>
                        <span>{reservations[0].creditCardExpiry}</span>
                    </div>
                </div>
            )}

            {onlineReception?.note && (
                <div className="hb-admin-note">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#e67e22" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <p className="hb-admin-note__content">{onlineReception.note}</p>
                </div>
            )}

            <div className="hb-details__action">
                <button className="hb-btn-reserve" disabled={loading} onClick={() => confirmReservations()}>
                    {loading ? translateBackend('Reserving...', language) : translateBackend('Reserve', language)}
                </button>
            </div>
        </div>
    );
};

export default OnlineReception;
